import React from 'react';
import { motion } from 'framer-motion';

const TechShoppingGuiltLanding = ({ goToInput, theme }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Tech Shopping Guilt: Break the Upgrade Cycle
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Calculate Your Tech Guilt Score</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Track your tech purchases, understand your upgrade patterns, and make mindful decisions about your devices.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Tech Purchase
            </motion.button>
          </div>
          <div className="text-8xl">📱</div>
        </div>
      </div>
      
      {/* What causes tech shopping guilt section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why Do We Feel Guilty About Tech Purchases?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💸</span>
              <div>
                <h3 className="font-bold dark:text-white">Premium Prices</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  The high cost of tech devices often creates financial anxiety and post-purchase guilt.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🔄</span>
              <div>
                <h3 className="font-bold dark:text-white">Upgrade Cycle</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  The pressure to constantly upgrade creates a cycle of premature purchases.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🌍</span>
              <div>
                <h3 className="font-bold dark:text-white">E-Waste Concerns</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  The environmental impact of discarded electronics creates ethical guilt.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">✨</span>
              <div>
                <h3 className="font-bold dark:text-white">Feature FOMO</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Fear of missing out on the latest features drives impulsive tech purchases.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Our Tool Helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Tech Guilt Calculator Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Upgrade Triggers
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Identify the emotional and marketing triggers that lead to impulsive tech purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Calculate True Value
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Evaluate cost-per-use and determine if the features you're paying for actually matter.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Get Personalized Tech Advice
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Receive tailored recommendations for guilt-free tech purchases based on your actual needs.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Develop Sustainable Tech Habits
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Learn to make mindful technology choices that balance innovation, budget, and environmental impact.
            </p>
          </div>
        </div>
      </section>
      
      {/* Tech Shopper Types Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Discover Your Tech Shopper Type</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🔄</span>
              <h3 className="font-bold mt-2 dark:text-white">Annual Upgrader</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You replace devices yearly regardless of need, leaving a trail of perfectly functional tech in your wake.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">📱</span>
              <h3 className="font-bold mt-2 dark:text-white">Spec Chaser</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You're always after the highest specs, even when they far exceed what you actually need for your usage.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🏷️</span>
              <h3 className="font-bold mt-2 dark:text-white">Deal Hunter</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You can't resist a tech bargain, often buying gadgets you don't need simply because they're on sale.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🧠</span>
              <h3 className="font-bold mt-2 dark:text-white">Mindful Adopter</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You research thoroughly, buy only what you need, and use devices for their full lifespan before upgrading.
            </p>
          </div>
        </div>
      </section>
      
      {/* Tech Guilt Stats */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Tech Shopping By The Numbers</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">2.5 yrs</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average smartphone lifespan before replacement</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$1,200+</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average cost of premium smartphones</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">68%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of people feel remorse after tech upgrades</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">57M tons</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">annual global e-waste generated</p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Others Improved Their Tech Buying Habits</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I used to upgrade my phone every year without thinking. After tracking my purchases, I realized I was spending over $1000 annually on marginal improvements I barely noticed."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Alex M.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Former Annual Upgrader</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "The guilt calculator helped me see I was buying tech to keep up with trends, not because I needed new features. Now I make a pros/cons list and wait 30 days before any major tech purchase."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Priya K.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Recovering Spec Chaser</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Tech Shopping Guilt</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do I feel guilty after buying new tech?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Tech guilt often stems from several factors: the high cost of devices, knowing your old device still worked, environmental concerns about e-waste, and the suspicion that you're caught in a marketing-driven upgrade cycle rather than buying based on genuine need.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do I know if I really need to upgrade my device?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Consider upgrading only when: your current device no longer performs tasks you need, repair costs exceed replacement value, security updates are no longer available, or battery life has significantly degraded and isn't replaceable. Track your tech needs with our guilt calculator.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I make more sustainable tech choices?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Extend device lifespan with proper maintenance, buy refurbished when possible, recycle old electronics properly, research companies with strong environmental policies, and consider repair-friendly brands. Our tech guilt calculator helps identify your sustainability priorities.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do I stop impulse-buying technology?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Implement a mandatory waiting period (at least 14 days) before purchases, unsubscribe from tech marketing emails, create a tech budget, make a needs vs. wants list for features, and track emotional triggers with our guilt calculator to understand your tech shopping patterns.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Make Smarter Tech Decisions?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your technology purchases and break free from the upgrade cycle.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your Tech Guilt Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Tech Shopping Guilt</h2>
          <p className="mb-3">
            Tech shopping guilt is the emotional discomfort experienced after purchasing new devices, often stemming from financial impact, knowing your old device still worked, concerns about electronic waste, or the feeling of being manipulated by marketing.
          </p>
          <p className="mb-3">
            Our Tech Guilt Calculator helps you understand and address these feelings by tracking your emotional state during purchases, analyzing your upgrade patterns, and providing personalized strategies to build a more mindful relationship with technology.
          </p>
          <p>
            Whether you're dealing with constant upgrade pressure, impulse buying new devices, or concerns about the environmental impact of your tech consumption, our free tool helps you develop healthier technology habits and reduce shopping guilt.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default TechShoppingGuiltLanding;
